// src/views/proprio/ProprioHistoriqueView.jsx — historique des factures proprio.
//
// Liste lecture seule des factures (table invoices, cloisonnées logIds côté
// serveur) émises pour les logements du proprio, filtrables par année. Aucune
// action (pas de génération / suppression — réservé admin).

import { useState, useEffect, useCallback, useRef } from 'react'
import { ClipboardList, Download, Eye, X } from 'lucide-react'
import { DS } from '../../lib/ui'
import { fmtE, fmtDate } from '../../lib/format'
import { invoiceTypeLabel } from '../../lib/invoices'
import { fetchProprioInvoices, fetchProprioInvoiceHtml, fetchProprioFacturationYears } from '../../lib/proprio-api'
import { SectionTitle, Loading, ErrorBox, EmptyBox, ReadOnlyHint } from './_proprioUi'
import useEchapPourFermer from "../../lib/useEchapPourFermer"

const CURRENT_YEAR = new Date().getFullYear()

// LE MONTANT D'UNE FACTURE — « — » quand il n'existe pas, JAMAIS « 0.00 € »
// (repris de #1269, 30/09). `fmtE(null)` rend « 0.00 € » (absence laissée à
// l'appelant) : sur un écran d'argent, un zéro fabriqué ne se voit pas et ne se
// corrige pas. Un VRAI 0 reste « 0.00 € » — zéro euro est une valeur légitime,
// l'absence de valeur ne l'est pas. Affichage seul : aucun montant recalculé.
export function montantFacture(amount) {
  if (amount == null || amount === '') return '—'
  return fmtE(amount)
}

// Les TROIS états d'une facture (spec §4, 2026-08-22) — servis par le serveur
// (`etat`) : « À RÉGLER » ambre · « RÉGLÉE » vert · « Annulée » gris.
const ETATS = {
  a_regler: { label: 'À RÉGLER', bg: '#fdf7ee', bord: '#e8d4b0', fg: '#8a5f19' },
  reglee: { label: 'RÉGLÉE', bg: '#f0f7f3', bord: '#c9e0d4', fg: '#124a33' },
  annulee: { label: 'Annulée', bg: '#f4f6f8', bord: '#e0e4e8', fg: '#5c6773' },
}

export default function ProprioHistoriqueView() {
  // Années du sélecteur = années où le proprio a RÉELLEMENT des factures (calculé
  // serveur, cloisonné logIds). Repli année courante → jamais un sélecteur vide.
  const [years, setYears] = useState([CURRENT_YEAR])
  const [year, setYear] = useState(CURRENT_YEAR)
  const [yearsReady, setYearsReady] = useState(false)
  const [state, setState] = useState({ loading: true, error: null, invoices: [] })

  // (1) Résout les années à afficher (mount). Le chargement des factures attend
  //   cette résolution → on ne fetch qu'une fois la bonne année par défaut connue.
  useEffect(() => {
    let alive = true
    fetchProprioFacturationYears()
      .then((res) => {
        if (!alive) return
        const ys = res.invoiceYears && res.invoiceYears.length ? res.invoiceYears : [CURRENT_YEAR]
        setYears(ys)
        setYear(ys[0])
      })
      .catch(() => { /* repli silencieux : année courante (sélecteur jamais vide) */ })
      .finally(() => { if (alive) setYearsReady(true) })
    return () => { alive = false }
  }, [])

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const res = await fetchProprioInvoices({ year })
      setState({ loading: false, error: null, invoices: res.invoices || [] })
    } catch (e) {
      setState({ loading: false, error: e.message, invoices: [] })
    }
  }, [year])

  useEffect(() => { if (yearsReady) load() }, [yearsReady, load])

  // Visionneuse d'UNE facture : { id, invoiceNumber } de la facture ouverte, son
  // html figé, l'état de chargement/erreur. Le html_snapshot est servi cloisonné
  // par le serveur (id ∧ logIds) — un id hors scope renvoie 404 (message neutre).
  const [viewer, setViewer] = useState({ open: false, loading: false, error: null, html: '', invoiceNumber: null })
  const iframeRef = useRef(null)

  const openInvoice = useCallback(async (inv) => {
    setViewer({ open: true, loading: true, error: null, html: '', invoiceNumber: inv.invoice_number || null })
    try {
      const res = await fetchProprioInvoiceHtml({ id: inv.id })
      const html = typeof res.html === 'string' ? res.html : ''
      if (!html) throw new Error('Facture indisponible.')
      setViewer({ open: true, loading: false, error: null, html, invoiceNumber: res.invoice_number || inv.invoice_number || null })
    } catch (e) {
      setViewer({ open: true, loading: false, error: e.message || 'Impossible d’ouvrir la facture.', html: '', invoiceNumber: inv.invoice_number || null })
    }
  }, [])

  // ⚠️ « TÉLÉCHARGER » DOIT TÉLÉCHARGER (Dimitri 2026-08-22) : le bouton
  // ouvrait seulement une visionneuse — rien n'arrivait dans les
  // téléchargements, alors qu'un propriétaire veut GARDER sa facture et
  // l'envoyer à son comptable. Le serveur sert le html figé de la facture :
  // on l'écrit dans un fichier, nommé par son numéro. L'aperçu reste
  // accessible par la ligne elle-même (et son bouton « Imprimer / PDF »).
  const [dlEnCours, setDlEnCours] = useState(null)
  const telechargerInvoice = useCallback(async (inv) => {
    setDlEnCours(inv.id)
    try {
      const res = await fetchProprioInvoiceHtml({ id: inv.id })
      const html = typeof res.html === 'string' ? res.html : ''
      if (!html) throw new Error('Facture indisponible.')
      const nom = (res.filename && String(res.filename))
        || `facture-${inv.invoice_number || inv.id}.html`
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nom
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      // Révocation différée : Safari lit l'URL APRÈS le clic.
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) {
      // Un échec ne reste jamais muet : la visionneuse porte le message.
      setViewer({ open: true, loading: false, error: e.message || 'Téléchargement impossible.',
        html: '', invoiceNumber: inv.invoice_number || null })
    } finally {
      setDlEnCours(null)
    }
  }, [])

  const closeViewer = useCallback(() => {
    setViewer({ open: false, loading: false, error: null, html: '', invoiceNumber: null })
  }, [])

  // Impression / export PDF : on cible le contentWindow de l'iframe (document figé
  // isolé), avec repli sur l'impression de la page hôte (comme RecuPage).
  const handlePrint = useCallback(() => {
    const frame = iframeRef.current
    if (frame && frame.contentWindow) {
      try {
        frame.contentWindow.focus()
        frame.contentWindow.print()
        return
      } catch (_e) { /* repli ci-dessous */ }
    }
    window.print()
  }, [])

  return (
    <div>
      <SectionTitle icon={ClipboardList} sub="Vos factures FULLBNBZEN.">Vos factures</SectionTitle>
      <ReadOnlyHint />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {years.map((yy) => (
          <button key={yy} onClick={() => setYear(yy)} className="fbz-tap fbz-press"
            style={{ padding: '6px 13px', borderRadius: DS.radiusControl, border: year === yy ? `0.5px solid ${DS.marine}` : `0.5px solid ${DS.borderHover}`, background: year === yy ? DS.marine : DS.card, color: year === yy ? '#fff' : DS.marine, fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: DS.fontBody }}>
            {yy}
          </button>
        ))}
      </div>

      {state.loading && <Loading />}
      {!state.loading && state.error && <ErrorBox message={state.error} onRetry={load} />}
      {!state.loading && !state.error && (
        <>
          {state.invoices.length === 0 && <EmptyBox>Aucune facture pour {year}.</EmptyBox>}
          {state.invoices.map((inv) => {
            const et = ETATS[inv.etat] || ETATS.reglee
            return (
              <div key={inv.id} data-testid={`facture-${inv.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px',
                  background: '#fff', border: '1px solid #e5eaf0', borderRadius: 12, marginBottom: 8 }}>
                {/* Pastille carrée 32 rayon 9 — la couleur suit l'état. */}
                <span aria-hidden="true"
                  style={{ width: 32, height: 32, borderRadius: 9, background: et.bg,
                    border: `1px solid ${et.bord}`, color: et.fg, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ClipboardList size={16} strokeWidth={1.9} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#233240' }}>
                    {inv.mois_label || fmtDate(inv.created_at)}
                    {/* Le logement de la facture — deux factures du même mois
                        se distinguent enfin (29/09). */}
                    {inv.logement_nom && (
                      <span data-testid={`facture-logement-${inv.id}`} style={{ fontWeight: 600, color: DS.textSecondary }}>
                        {' · '}{inv.logement_nom}
                      </span>
                    )}
                  </div>
                  <div style={{ fontFamily: DS.fontDisplay, fontSize: 14, fontWeight: 700, color: DS.marineDark, marginTop: 1 }}>
                    {montantFacture(inv.amount)}
                  </div>
                  <div style={{ fontSize: 10.5, color: DS.textSecondary, marginTop: 1 }}>
                    {inv.invoice_number}
                    {inv.invoice_type ? ` · ${invoiceTypeLabel(inv.invoice_type)}` : ''}
                  </div>
                </div>
                <span style={{ background: et.bg, color: et.fg, border: `1px solid ${et.bord}`,
                  borderRadius: 7, padding: '3px 9px', fontSize: 10, fontWeight: 700,
                  letterSpacing: '.03em', flexShrink: 0 }}>{et.label}</span>
                {/* APERÇU — l'écran garde son chemin de lecture (le bouton
                    voisin, lui, TÉLÉCHARGE vraiment depuis le 2026-08-22). */}
                {inv.status === 'sent' && (
                  <button
                    type="button"
                    onClick={() => openInvoice(inv)}
                    className="fbz-tap fbz-press"
                    title={`Voir la facture ${inv.invoice_number || ''}`.trim()}
                    aria-label={`Voir la facture ${inv.invoice_number || ''}`.trim()}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 34, height: 34, borderRadius: 9, border: '1px solid #d5e3ee',
                      background: '#fff', color: '#5c8ab8', cursor: 'pointer', flexShrink: 0 }}
                  >
                    <Eye size={15} strokeWidth={2} />
                  </button>
                )}
                {/* Téléchargement : seules les factures émises ont un html figé. */}
                {inv.status === 'sent' && (
                  <button
                    type="button"
                    onClick={() => telechargerInvoice(inv)}
                    disabled={dlEnCours === inv.id}
                    className="fbz-tap fbz-press"
                    title={`Télécharger la facture ${inv.invoice_number || ''}`.trim()}
                    aria-label={`Télécharger la facture ${inv.invoice_number || ''}`.trim()}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 34, height: 34, borderRadius: 9, border: '1px solid #d5e3ee',
                      background: '#fff', color: '#5c8ab8', cursor: 'pointer', flexShrink: 0 }}
                  >
                    <Download size={15} strokeWidth={2} />
                  </button>
                )}
              </div>
            )
          })}
        </>
      )}

      {viewer.open && (
        <InvoiceViewerModal
          viewer={viewer}
          iframeRef={iframeRef}
          onPrint={handlePrint}
          onClose={closeViewer}
        />
      )}
    </div>
  )
}

// Visionneuse plein écran d'une facture figée. Le html_snapshot est un document
// HTML self-contained produit par NOTRE cron (de confiance) → isolé en iframe
// SANDBOXÉE `allow-same-origin` SANS allow-scripts (scripts inertes = XSS neutralisé,
// CSS du document isolé de la page). `allow-same-origin` requis pour window.print().
// Même posture que src/RecuPage.jsx. Utilisable en 375px (bandeau boutons + iframe
// pleine largeur).
function InvoiceViewerModal({ viewer, iframeRef, onPrint, onClose }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Facture"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(27,58,92,.55)', display: 'flex', flexDirection: 'column', padding: '12px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: DS.card, borderRadius: 14, boxShadow: '0 10px 40px rgba(27,58,92,.28)', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 820, margin: '0 auto', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 14px', borderBottom: `0.5px solid ${DS.borderHover}`, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 14 }}>
            {viewer.invoiceNumber || 'Facture'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={onPrint}
              disabled={viewer.loading || !!viewer.error || !viewer.html}
              className="fbz-tap fbz-press"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: DS.radiusControl, border: 'none', background: DS.marine, color: '#fff', fontWeight: 600, fontSize: 12, cursor: viewer.html ? 'pointer' : 'not-allowed', opacity: viewer.html ? 1 : 0.55, fontFamily: DS.fontBody }}
            >
              <Download size={14} /> Imprimer / PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="fbz-tap fbz-press"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, background: '#f0f4f9' }}>
          {viewer.loading && <div style={{ padding: 24 }}><Loading /></div>}
          {!viewer.loading && viewer.error && (
            <div style={{ padding: 20 }}>
              <ErrorBox message={viewer.error} />
            </div>
          )}
          {!viewer.loading && !viewer.error && viewer.html && (
            <iframe
              ref={iframeRef}
              title="Facture"
              srcDoc={viewer.html}
              sandbox="allow-same-origin"
              data-testid="proprio-invoice-frame"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
